import type { ReactElement } from 'react';
import { Badge, Box, Grid, Group, Stack, Text, UnstyledButton } from '@mantine/core';
import { ArrowRight, Minus, TrendingDown, TrendingUp } from 'lucide-react';
import { SurfaceCard } from '@/design/SurfaceCard';
import Sparkline from '@/design/Sparkline/Sparkline';
import { adoptionBand } from './AdoptionBands';
import AdoptionLevelDistribution from './AdoptionLevelDistribution';
import type { AdoptionBreakdownRow, AdoptionScore } from './AdoptionTypes';

interface AdoptionScoreHeroProps {
    score: AdoptionScore;
    /** Scored breakdown rows (incl. inactive) powering the adoption-by-level distribution. */
    breakdown: AdoptionBreakdownRow[];
    onOpenDrivers: () => void;
}

/** The 190px circular adoption gauge with the composite value and maturity band at its center. */
function Gauge({ value, color, band }: { value: number; color: string; band: string }): ReactElement {
    const r = 78;
    const c = 2 * Math.PI * r;
    const arc = (c * value) / 100;
    return (
        <Box style={{ position: 'relative', width: 190, height: 190, flexShrink: 0 }}>
            <svg width={190} height={190} viewBox="0 0 190 190">
                <circle cx={95} cy={95} r={r} fill="none" stroke="var(--bm-color-surface)" strokeWidth={15} />
                <circle
                    cx={95}
                    cy={95}
                    r={r}
                    fill="none"
                    stroke={color}
                    strokeWidth={15}
                    strokeLinecap="round"
                    strokeDasharray={`${arc.toFixed(1)} ${(c - arc).toFixed(1)}`}
                    transform="rotate(-90 95 95)"
                />
            </svg>
            <Stack gap={4} align="center" justify="center" style={{ position: 'absolute', inset: 0 }}>
                <Text fw={700} ff="monospace" style={{ fontSize: 44, lineHeight: 1, letterSpacing: '-0.03em' }}>
                    {value}
                </Text>
                <Badge variant="light" radius="xl" styles={{ root: { background: adoptionBand(value).soft, color }, label: { fontWeight: 700 } }}>
                    {band}
                </Badge>
                <Text c="dimmed" style={{ fontSize: 11 }}>
                    out of 100
                </Text>
            </Stack>
        </Box>
    );
}

function Delta({ pts }: { pts: number }): ReactElement {
    const dir = pts > 0 ? 'up' : pts < 0 ? 'down' : 'flat';
    const color = dir === 'up' ? 'var(--bm-color-success)' : dir === 'down' ? 'var(--bm-color-danger)' : 'var(--bm-color-text-dimmed)';
    const Icon = dir === 'up' ? TrendingUp : dir === 'down' ? TrendingDown : Minus;
    return (
        <Group gap={4} wrap="nowrap" align="center" style={{ color }}>
            <Icon size={15} />
            <Text fw={700} style={{ fontSize: 13 }}>
                {pts > 0 ? '+' : ''}
                {pts} pts
            </Text>
        </Group>
    );
}

export default function AdoptionScoreHero({ score, breakdown, onOpenDrivers }: AdoptionScoreHeroProps): ReactElement {
    const band = adoptionBand(score.score);
    const { context } = score;
    const pctFill = Math.max(0, Math.min(100, score.percentile));

    return (
        <Grid gutter="md" align="stretch">
            <Grid.Col span={{ base: 12, lg: 7 }}>
                <UnstyledButton onClick={onOpenDrivers} style={{ display: 'block', width: '100%', height: '100%', cursor: 'pointer' }} aria-label="View the four drivers and trends">
                    <SurfaceCard style={{ height: '100%', display: 'flex', flexDirection: 'column', borderTop: `2px solid ${band.color}`, cursor: 'pointer' }}>
                        <Group justify="space-between" align="flex-start" mb="sm">
                            <Box>
                                <Text fw={700} style={{ fontSize: 15 }}>
                                    Overall Adoption Score
                                </Text>
                                <Text size="xs" c="dimmed" mt={4} maw={460}>
                                    A composite of Volume, Frequency, Complexity and Consistency, normalized 0–100 and banded by maturity.
                                </Text>
                            </Box>
                        </Group>

                        <Group align="center" gap={28} wrap="nowrap">
                            <Gauge value={score.score} color={band.color} band={band.name} />
                            <Stack gap="sm" style={{ flex: 1, minWidth: 0 }}>
                                <Group gap="sm" align="center" wrap="nowrap">
                                    <Delta pts={score.deltaPts} />
                                    <Text c="dimmed" style={{ fontSize: 12.5 }}>
                                        vs. previous 4 weeks
                                    </Text>
                                </Group>

                                <Sparkline values={score.weekly} color={band.color} height={60} />

                                <Box style={{ background: 'var(--bm-color-surface)', border: '1px solid var(--bm-color-border-subtle)', borderRadius: 'var(--bm-radius)', padding: '13px 15px' }}>
                                    <Group justify="space-between" mb={10} align="baseline">
                                        <Text size="xs" c="dimmed">
                                            Percentile vs. peer teams
                                        </Text>
                                        <Text fw={700} style={{ fontSize: 15 }}>
                                            {score.percentile}
                                            <Text span c="dimmed" fw={500} style={{ fontSize: 11 }}>
                                                th pct
                                            </Text>
                                        </Text>
                                    </Group>
                                    <Box style={{ position: 'relative', height: 10, background: 'var(--bm-color-surface-alt)', borderRadius: 999 }}>
                                        <Box
                                            style={{
                                                position: 'absolute',
                                                left: 0,
                                                top: 0,
                                                bottom: 0,
                                                width: `${pctFill}%`,
                                                minWidth: 10,
                                                borderRadius: 999,
                                                background: 'linear-gradient(90deg, var(--mantine-color-brand-5), var(--mantine-primary-color-filled))',
                                            }}
                                        />
                                        <Box style={{ position: 'absolute', top: -3, bottom: -3, left: `${score.benchmarkMedian}%`, width: 2, borderRadius: 2, background: 'var(--bm-color-text-dimmed)', opacity: 0.45 }} />
                                        <Box style={{ position: 'absolute', top: -3, bottom: -3, left: `${score.benchmarkTopQuartile}%`, width: 2, borderRadius: 2, background: 'var(--bm-color-caution)', opacity: 0.7 }} />
                                        <Box
                                            style={{
                                                position: 'absolute',
                                                top: '50%',
                                                left: `${pctFill}%`,
                                                transform: 'translate(-50%, -50%)',
                                                width: 14,
                                                height: 14,
                                                borderRadius: '50%',
                                                background: 'var(--bm-color-body)',
                                                border: '3px solid var(--mantine-primary-color-filled)',
                                            }}
                                        />
                                    </Box>
                                    <Group justify="space-between" mt={10}>
                                        <Group gap={12} wrap="nowrap">
                                            <Group gap={5} wrap="nowrap">
                                                <Box style={{ width: 8, height: 2, borderRadius: 2, background: 'var(--bm-color-text-dimmed)', opacity: 0.55 }} />
                                                <Text size="xs" c="dimmed">
                                                    Median {score.benchmarkMedian}
                                                </Text>
                                            </Group>
                                            <Group gap={5} wrap="nowrap">
                                                <Box style={{ width: 8, height: 2, borderRadius: 2, background: 'var(--bm-color-caution)' }} />
                                                <Text size="xs" c="dimmed">
                                                    Top quartile {score.benchmarkTopQuartile}
                                                </Text>
                                            </Group>
                                        </Group>
                                        <Text c="dimmed" fw={500} style={{ fontSize: 11 }}>
                                            illustrative
                                        </Text>
                                    </Group>
                                </Box>
                            </Stack>
                        </Group>

                        <Group gap={6} mt="auto" pt="md" justify="flex-end" style={{ color: band.color }}>
                            <Text fw={600} style={{ fontSize: 12 }}>
                                View the four drivers and trends
                            </Text>
                            <ArrowRight size={14} />
                        </Group>
                    </SurfaceCard>
                </UnstyledButton>
            </Grid.Col>

            <Grid.Col span={{ base: 12, lg: 5 }}>
                <Stack gap="md" style={{ height: '100%' }}>
                    <AdoptionLevelDistribution rows={breakdown} />

                    <SurfaceCard style={{ flex: 1 }}>
                        <Text fw={700} style={{ fontSize: 15 }} mb={8}>
                            How the score is built
                        </Text>
                        <Text size="xs" c="dimmed" lh={1.5}>
                            Score ={' '}
                            <Text span fw={700} c="var(--bm-color-text)">
                                0.30·Frequency + 0.25·Consistency + 0.25·Complexity + 0.20·Activity
                            </Text>
                            . Activity is weighted lowest on purpose: rewarding raw tokens would reward verbosity and penalize the right-sizing this product drives. Weights are tunable per
                            tenant. Where a provider can&apos;t supply an axis, the score re-weights over what&apos;s available and coverage drops below {context.coverageTotal} of{' '}
                            {context.coverageTotal}.
                        </Text>
                    </SurfaceCard>
                </Stack>
            </Grid.Col>
        </Grid>
    );
}
